import React, { useEffect } from 'react'
import { View } from 'react-native'
import { useDispatch, useSelector } from 'APP/Hooks/redux'
import { useNavigation } from '@react-navigation/native'
import { Trans } from 'react-i18next'
import styled from 'styled-components/native'
import { SafeAreaView } from 'react-native-safe-area-context'

import { Images } from 'APP/Themes'
import I18n from 'APP/I18n'
import { openInbox } from 'APP/Lib/EmailLinking'
import { useDevTools } from 'APP/Lib/DevTools'

import LoginActions from 'APP/Redux/LoginRedux'
import PatientActions from 'APP/Redux/PatientRedux'
import usePrevious from 'APP/Hooks/usePrevious'
import useCountdown from 'APP/Hooks/useCountdown'
import { isMobile } from 'APP/Helpers/checkPlatform'

import Button from 'APP/Converse/Button'
import LogoHeader from 'APP/Components/LogoHeader'
import Typography from 'APP/Converse/Typography'
import { ResponsiveView } from 'APP/Converse/Layout'

const ScreenContainer = styled(SafeAreaView).attrs({
  edges: ['bottom', 'left', 'right'],
})`
  flex: 1;
`

const ContentContainer = styled(ResponsiveView)`
  gap: ${({ theme }) => theme.metrics.baseMargin * 2}px;
  padding: ${({ theme }) => theme.metrics.baseMargin}px;
`

const ButtonContainer = styled.View`
  gap: ${({ theme }) => theme.metrics.baseMargin}px;
`

const VerifyEmailScreen = () => {
  const navigation = useNavigation()
  const dispatch = useDispatch()
  const { setToolName, setToolFunction } = useDevTools()
  const { count: emailVerificationBlockTimeout, start: startTimeout } = useCountdown()
  const { emailVerificationRunning, emailVerificationCheckSilent } = useSelector(
    (state) => state.login
  )

  const { email } = useSelector((state) => state.login?.customClaims)

  useEffect(() => {
    setToolName('Skip')
    setToolFunction(() => () => {
      dispatch(PatientActions.skipEmailVerification())
    })
    return () => {
      setToolFunction(null)
      setToolName(null)
    }
  }, [dispatch, setToolName, setToolFunction])

  const prevEmailVerificationRunning = usePrevious(emailVerificationRunning)

  useEffect(() => {
    if (
      prevEmailVerificationRunning &&
      !emailVerificationCheckSilent &&
      !emailVerificationRunning
    ) {
      startTimeout(5)
    }
  }, [emailVerificationRunning, emailVerificationCheckSilent, prevEmailVerificationRunning])

  useEffect(() => {
    dispatch(
      LoginActions.setEmailVerificationBlock(
        (emailVerificationRunning && !emailVerificationCheckSilent) ||
          !!emailVerificationBlockTimeout
      )
    )
  }, [
    dispatch,
    emailVerificationRunning,
    emailVerificationCheckSilent,
    emailVerificationBlockTimeout,
  ])

  const requestEmailVerificationCheck = () => {
    dispatch(LoginActions.requestEmailVerificationCheck())
  }

  const subtitle = (
    <Typography>
      {
        <Trans i18n={I18n} i18nKey="VerifyEmailScreen.subtitle" values={{ email }}>
          <Typography variant="bodyNormalBold">{{ email }}</Typography>
        </Trans>
      }
    </Typography>
  )

  return (
    <ScreenContainer>
      <ContentContainer>
        <LogoHeader
          source={Images.logoBlue}
          title={I18n.t('VerifyEmailScreen.title')}
          subtitle={subtitle}
        />
        <ButtonContainer>
          {isMobile() && (
            <Button
              testID="OPEN_INBOX_BTN"
              variant="primary"
              widthVariant="full"
              onPress={() => openInbox()}
              title={I18n.t('VerifyEmailScreen.openEmailApp')}
            />
          )}
          <Button
            testID="VERIFY_EMAIL_BTN"
            variant="secondary"
            widthVariant="full"
            onPress={requestEmailVerificationCheck}
            title={I18n.t('VerifyEmailScreen.verify')}
            isLoading={emailVerificationRunning && !emailVerificationCheckSilent}
            analyticsName="VerifyEmailScreen.verify"
            disabled={
              (emailVerificationRunning && !emailVerificationCheckSilent) ||
              !!emailVerificationBlockTimeout
            }
          />
          {!!emailVerificationBlockTimeout && (
            <View testID="VERIFICATION_BLOCK_TIMEOUT">
              <Typography variant="bodyNormal" align="center">
                {I18n.t('VerifyEmailScreen.notVerified')}
              </Typography>
              <Typography variant="bodyNormal" align="center">
                {I18n.t('VerifyEmailScreen.verificationBlock', {
                  seconds: emailVerificationBlockTimeout,
                })}
              </Typography>
            </View>
          )}
          <Button
            testID="NEED_HELP_BTN"
            variant="tertiary"
            widthVariant="full"
            onPress={() => navigation.navigate('verifyEmailHelp')}
            title={I18n.t('VerifyEmailScreen.needHelp')}
            analyticsName="VerifyEmailScreen.verify"
          />
        </ButtonContainer>
      </ContentContainer>
    </ScreenContainer>
  )
}

export default VerifyEmailScreen
